// API service for communicating with Admin Area API (AWS Lambda)
const API_BASE_URL = (
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_ADMIN_API_URL ||
  'https://5kv7hzzg97.execute-api.us-east-1.amazonaws.com/staging'
).replace(/\/$/, '');

class ApiService {
  constructor() {
    this.baseURL = API_BASE_URL;
  }

  async makeRequest(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Public endpoints that don't require authentication
    const publicEndpoints = ['/signup', '/phill', '/health', '/auth/login', '/confirm-email'];
    const isPublicEndpoint = publicEndpoints.includes(endpoint);
    
    // Get access token from our custom auth service if not a public endpoint
    let authHeaders = {};
    if (!isPublicEndpoint) {
      try {
        // Import authService dynamically to avoid circular dependency
        const authService = (await import('./authService')).default;
        const { resolveActiveMasterAccountId } = await import('../tenant/activeMasterAccount');
        const token = await authService.getValidAccessToken();
        if (token) {
          authHeaders = {
            'Authorization': `Bearer ${token}`
          };
          const activeMa = resolveActiveMasterAccountId(token);
          if (activeMa) {
            authHeaders['X-KK-Active-Master-Account-Id'] = activeMa;
          }
        } else {
          throw new Error(`Authentication required for ${endpoint}`);
        }
      } catch (error) {
        console.warn('Failed to get access token:', error);
        throw error;
      }
    }
    
    const defaultOptions = {
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options.headers,
      },
    };

    const requestOptions = {
      ...defaultOptions,
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...(options.headers || {}),
      },
    };

    try {
      const response = await fetch(url, requestOptions);
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        const err = new Error(errorData.message || `HTTP error! status: ${response.status}`);
        err.status = response.status;
        err.payload = errorData;
        err.code = errorData.code;
        throw err;
      }
      
      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  // Create a new company (this will be our signup endpoint for now)
  async createCompany(companyData) {
    // Transform signup form data to match API expectations
    const apiData = {
      name: companyData.companyName,
      lltId: companyData.lltId || `llt-${Date.now()}`, // Generate temporary LLT ID
      address_street: companyData.companyAddress,
      address_city: companyData.city,
      address_state: companyData.state,
      address_zipCode: companyData.zipCode,
      address_country: 'United States'
    };

    return this.makeRequest('/company', {
      method: 'POST',
      body: JSON.stringify(apiData),
    });
  }

  // Register a new user (regular signup)
  async registerUser(userData) {
    return this.makeRequest('/signup', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  }

  // Register a new vendor (vendor signup)  
  async registerVendor(vendorData) {
    return this.makeRequest('/signup', {
      method: 'POST',
      body: JSON.stringify(vendorData),
    });
  }

  // Test endpoint to verify API connectivity
  async testConnection() {
    return this.makeRequest('/phill', {
      method: 'GET',
    });
  }

  // PR 3 — Music Provider holding pipeline (Commercial structured metadata)
  async initMusicUpload({
    fileName,
    contentType,
    fileSizeBytes,
    title,
    artistName,
    album,
    recordLabelName,
    isrc,
  }) {
    const body = { fileName, contentType, fileSizeBytes };
    if (title) body.title = title;
    if (artistName) body.artistName = artistName;
    if (album) body.album = album;
    if (recordLabelName) body.recordLabelName = recordLabelName;
    if (isrc) body.isrc = isrc;
    return this.makeRequest('/me/music-uploads', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  async ackMusicUploadParts(uploadId, parts) {
    return this.makeRequest(
      `/me/music-uploads/${encodeURIComponent(uploadId)}/parts/ack`,
      {
        method: 'POST',
        body: JSON.stringify({ parts }),
      },
    );
  }

  async completeMusicUpload(uploadId) {
    return this.makeRequest(`/me/music-uploads/${encodeURIComponent(uploadId)}/complete`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
  }

  async listMusicUploads({ includeInitiated = false } = {}) {
    const qs = includeInitiated ? '?includeInitiated=true' : '';
    return this.makeRequest(`/me/music-uploads${qs}`, {
      method: 'GET',
    });
  }

  async getMusicUpload(uploadId) {
    return this.makeRequest(`/me/music-uploads/${encodeURIComponent(uploadId)}`, {
      method: 'GET',
    });
  }

  // Client channel preferences (Approved / Declined Live channels)
  async listMeChannels() {
    return this.makeRequest('/me/channels', {
      method: 'GET',
    });
  }

  async approveMeChannel(channelId) {
    return this.makeRequest(
      `/me/channels/${encodeURIComponent(channelId)}/approve`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
    );
  }

  async declineMeChannel(channelId) {
    return this.makeRequest(
      `/me/channels/${encodeURIComponent(channelId)}/decline`,
      {
        method: 'POST',
        body: JSON.stringify({}),
      },
    );
  }

  /**
   * Durable Production next-track for a Zone (least-played staged algorithm).
   * @param {string} channelId
   * @param {{ zoneId: string, action: 'start'|'skip'|'ended' }} body
   */
  async meChannelPlaybackNext(channelId, body) {
    return this.makeRequest(
      `/me/channels/${encodeURIComponent(channelId)}/playback/next`,
      {
        method: 'POST',
        body: JSON.stringify(body || {}),
      },
    );
  }

  /**
   * Brand-scoped Support context (SUPPORT-QA-01): `?brandId=` tells the API
   * which Brand the operator is inside. The API enforces the boundary
   * (403 BRAND_RESTRICTED / TICKET_WRONG_BRAND_CONTEXT); this is never
   * a client-side-only filter.
   */
  supportBrandQuery(brandId) {
    const brand = String(brandId || "").trim();
    return brand ? `?brandId=${encodeURIComponent(brand)}` : "";
  }

  /**
   * Client support tickets for the active Master Account.
   * No brandId → Client / Brand Manager hub (account-level + authorized Brands).
   * brandId → only that Brand's Cases.
   */
  async listSupportTickets({ brandId = null } = {}) {
    return this.makeRequest(
      `/me/support-tickets${this.supportBrandQuery(brandId)}`,
      { method: 'GET' },
    );
  }

  /** Create a Client support ticket (business case). brandId optional. */
  async createSupportTicket({ summary, brandId = null }) {
    const body = { summary };
    const brand = String(brandId || "").trim();
    if (brand) body.brandId = brand;
    return this.makeRequest('/me/support-tickets', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  /** List canonical Support conversation messages for a case. */
  async listSupportMessages(ticketId, { brandId = null } = {}) {
    return this.makeRequest(
      `/me/support-tickets/${encodeURIComponent(ticketId)}/messages${this.supportBrandQuery(brandId)}`,
      { method: 'GET' },
    );
  }

  /** Reply on a Support case (persisted before any email transport). */
  async postSupportMessage(ticketId, { bodyText, brandId = null }) {
    return this.makeRequest(
      `/me/support-tickets/${encodeURIComponent(ticketId)}/messages${this.supportBrandQuery(brandId)}`,
      {
        method: 'POST',
        body: JSON.stringify({ bodyText }),
      },
    );
  }

  /**
   * Client closes their OWN Open Support Case (Owner decision). Same canonical
   * Closed lifecycle as a staff close: final, no reopen, no replies, CSAT
   * available, further help = new Case. No closure note is sent.
   */
  async closeSupportTicket(ticketId, { brandId = null } = {}) {
    return this.makeRequest(
      `/me/support-tickets/${encodeURIComponent(ticketId)}/close${this.supportBrandQuery(brandId)}`,
      { method: 'POST', body: JSON.stringify({}) },
    );
  }

  /** Closed-Case satisfaction response for a Support Case (null until submitted). */
  async getSupportCsat(ticketId, { brandId = null } = {}) {
    return this.makeRequest(
      `/me/support-tickets/${encodeURIComponent(ticketId)}/csat${this.supportBrandQuery(brandId)}`,
      { method: 'GET' },
    );
  }

  /**
   * Submit the Closed-Case satisfaction survey (rating 1–5, optional feedback).
   * One response per Case; never changes the Case status.
   */
  async submitSupportCsat(
    ticketId,
    { rating, feedbackText = null, brandId = null, via = 'ma-ui' },
  ) {
    const body = { rating, via };
    const text = String(feedbackText || '').trim();
    if (text) body.feedbackText = text;
    return this.makeRequest(
      `/me/support-tickets/${encodeURIComponent(ticketId)}/csat${this.supportBrandQuery(brandId)}`,
      { method: 'POST', body: JSON.stringify(body) },
    );
  }

  /** Active Super Admin Brands available to search/add (directory only). */
  async listBrandCatalog() {
    return this.makeRequest('/me/brands/catalog', {
      method: 'GET',
    });
  }

  /** Brands this Master Account already has membership on. */
  async listMyBrands() {
    return this.makeRequest('/me/brands/mine', {
      method: 'GET',
    });
  }

  /**
   * Portfolio Locations this Master Account currently operates.
   * Optional brandId filters Brand-shell views.
   * Inventory = LocationAssignment; WHERE = MAAL (UBA/UBLE).
   */
  async listMyLocations({ brandId } = {}) {
    const params = new URLSearchParams();
    const id = String(brandId || '').trim();
    if (id) params.set('brandId', id);
    const qs = params.toString();
    return this.makeRequest(`/me/locations${qs ? `?${qs}` : ''}`, {
      method: 'GET',
    });
  }

  /**
   * Create a Brand-owned Location + operated LocationAssignment for the active MA.
   * Owner/Admin only (enforced by API). Requires Idempotency-Key.
   */
  async createMyLocation(body, { idempotencyKey, activeBrandId } = {}) {
    const key = String(idempotencyKey || body?.idempotencyKey || '').trim();
    if (!key) {
      throw new Error('idempotencyKey is required');
    }
    const brandId = String(activeBrandId || body?.brandId || '').trim();
    if (!brandId) {
      throw new Error('activeBrandId is required');
    }
    return this.makeRequest('/me/locations', {
      method: 'POST',
      headers: {
        'Idempotency-Key': key,
        'X-KK-Active-Brand-Id': brandId,
      },
      body: JSON.stringify({ ...body, brandId, idempotencyKey: key }),
    });
  }

  /**
   * Self-join a platform Brand at Limited Brand Access Level.
   * Full access is Super Admin–only.
   */
  async joinBrand(brandId) {
    return this.makeRequest('/me/brands/join', {
      method: 'POST',
      body: JSON.stringify({ brandId }),
    });
  }

  /** Remove a Brand from this Master Account's Brand Manager. */
  async leaveBrand(brandId) {
    return this.makeRequest('/me/brands/leave', {
      method: 'POST',
      body: JSON.stringify({ brandId }),
    });
  }

  /** Brand-scoped user roster from canonical UserBrandAccess. */
  async listBrandUsers(brandId) {
    const id = String(brandId || '').trim();
    if (!id) {
      throw new Error('brandId is required');
    }
    const raw = await this.makeRequest(
      `/me/brand-users/${encodeURIComponent(id)}`,
      {
        method: 'GET',
      },
    );
    // Handler returns `{ success: true, data: { users, ... } }`.
    return raw?.data ?? raw;
  }

  /**
   * Client / Brand Manager portfolio user roster from UserMembership
   * (active Master Account only). Brands column from UserBrandAccess.
   */
  async listPortfolioUsers() {
    const raw = await this.makeRequest('/me/users', {
      method: 'GET',
    });
    return raw?.data ?? raw;
  }
}

export default new ApiService();
